import { EnvironmentMode } from 'src/shared/environment-types';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';

export class GroupEntry {
	public debugName: string;

	constructor(debugName: string) {
		this.debugName = debugName;
	}
}

export class GroupData {
	public entries: Map<string, GroupEntry>;

	constructor() {
		this.entries = new Map<string, GroupEntry>();
	}

	public get quantity(): number {
		let total = 0;

		for (const count of this.entries.values()) {
			total += 1;
		}

		return total;
	}
}

export default class GroupsCounter<TGroupType> {

	private _groupsMap: Map<TGroupType, Map<string, GroupData>> = new Map<TGroupType, Map<string, GroupData>>();

	public hasActiveEntries(groupType: TGroupType): boolean {
		const groupMap: Map<string, GroupData> = this._groupsMap.get(groupType);

		if (groupMap) {
			for (const [, groupData] of groupMap) {
				if (groupData.quantity > 0) {
					return true; // At least one group has a quantity greater than 0.
				}
			}
		}

		return false;
	}

	private _onIncreaseMap: Map<TGroupType, BehaviorSubject<Map<string, GroupData>>> = new Map<TGroupType, BehaviorSubject<Map<string, GroupData>>>();

	public getOnIncreaseSubject(groupType: TGroupType): BehaviorSubject<Map<string, GroupData>> {
		if (!this._onIncreaseMap.has(groupType)) {
			this._onIncreaseMap.set(groupType, new BehaviorSubject<Map<string, GroupData>>(new Map<string, GroupData>()));
		}

		return this._onIncreaseMap.get(groupType)!;
	}

	public increase(groupType: TGroupType, groupId: string, debugName: string = null, debug: boolean = true): void {
		if (!this._groupsMap.has(groupType)) {
			this._groupsMap.set(
				groupType,
				new Map<string, GroupData>()
			);
		}

		const groupMap: Map<string, GroupData> = this._groupsMap.get(groupType);

		let groupData: GroupData = groupMap.get(groupId);

		if (!groupData) {
			groupData = new GroupData();

			groupMap.set(
				groupId,
				groupData
			);
		}

		groupData.entries.set(
			debugName,
			new GroupEntry(debugName)
		);

		if (debug && environment.mode !== EnvironmentMode.PRODUCTION) {
			const message = `increase -> groupType: ${groupType} | groupId: ${groupId} | quantity: ${groupData.quantity} - new value: ${groupData.quantity + 1}. {${debugName ? debugName : ""}}`;

			const logData = {
				message: message,
				title: "\nGroup Data:",
				data: {
					quantity: groupData.quantity,
					debugNames: Array.from(groupData.entries.values()).map((groupEntry) => groupEntry.debugName)
				}
			};

			if (groupData.quantity >= 0) {
				console.log(
					logData.message,
					logData.title,
					logData.data
				);
			} else {
				console.warn(
					logData.message,
					logData.title,
					logData.data
				);
			}
		}

		this.getOnIncreaseSubject(groupType).next(groupMap);
	}

	private _onDecreaseMap: Map<TGroupType, BehaviorSubject<Map<string, GroupData>>> = new Map<TGroupType, BehaviorSubject<Map<string, GroupData>>>();

	public getOnDecreaseSubject(groupType: TGroupType): BehaviorSubject<Map<string, GroupData>> {
		if (!this._onDecreaseMap.has(groupType)) {
			this._onDecreaseMap.set(groupType, new BehaviorSubject<Map<string, GroupData>>(new Map<string, GroupData>()));
		}

		return this._onDecreaseMap.get(groupType)!;
	}

	public decrease(groupType: TGroupType, groupId: string, debugName: string = null, debug: boolean = true): void {
		if (!this._groupsMap.has(groupType)) {
			this._groupsMap.set(
				groupType,
				new Map<string, GroupData>()
			);
		}

		const groupMap: Map<string, GroupData> = this._groupsMap.get(groupType);

		let groupData: GroupData = groupMap.get(groupId);

		if (!groupData) {
			console.warn(`Data(groupId) for decrease doesn't exist. { groupType: ${groupType} | groupId: ${groupId} }`);

			return;
		}

		if (!groupData.entries.delete(debugName)){
			console.warn(`Data(debugName) for decrease doesn't exist. { groupType: ${groupType} | groupId: ${groupId} | debugName: ${debugName} }`);

			return;
		}

		if (debug && environment.mode !== EnvironmentMode.PRODUCTION) {
			const message = `increase -> groupType: ${groupType} | groupId: ${groupId} | quantity: ${groupData.quantity} - new value: ${groupData.quantity - 1}. {${debugName ? debugName : ""}}`;

			const logData = {
				message: message,
				title: "\nGroup Data:",
				data: {
					quantity: groupData.quantity,
					debugNames: Array.from(groupData.entries.values()).map((groupEntry) => groupEntry.debugName)
				}
			};

			if (groupData.quantity >= 0) {
				console.log(
					logData.message,
					logData.title,
					logData.data
				);
			} else {
				console.warn(
					logData.message,
					logData.title,
					logData.data
				);
			}
		}

		this.getOnDecreaseSubject(groupType).next(groupMap);
	}
}
